import React, { ChangeEvent, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Info,
  Palette,
  X,
  Image
} from "lucide-react";
import ProductVariantManager from "./ProductVariantManager";
import useToastStore from "../../store/useToastStore";
import { ProductService } from "../../services/productService";
import { Product } from "../../types/product";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css"; // Import Quill styles
import { useAuth } from "../../hooks/useAuth";
import { trackEvent } from "../../utils/analyticsHelper";
import useEntity from "../../hooks/useEntity";
import { setMetaUser, trackMetaCustom } from "../../config/metaPixel";
import { clearInventoryStatsCache } from "../../utils/inventoryStats";
import { queryClient } from "../../config/query";


// Mock components for demonstration
const Loading = () => (
  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
);

type VariantOption = {
  id?: string; // Optional ID for existing variants
  attributes: Record<string, string>;
  mrp: number;
  discountPercent: number;
  stock: number;
  sku: string;
  existingImages: string[]; // Existing URLs from backend
  images: File[];           // New uploads
  discountedPrice: number;
};

type ProductVariantConfig = {
  hasVariants: boolean;
  attributes: string[];
  variantOptions: Record<string, string[]>;
  variants: VariantOption[];
};

type ProductForm = {
  title: string;
  description: string;
  author?: string;
  mrp?: number | string;
  sellingPrice?: number | string;
  sku?: string;
  stock?: number | string;
  deliveryCharge?: number | string;
  images?: File[];
  hasVariants: boolean;
  variantConfig?: ProductVariantConfig;
  minOrderQty?: number | string;
};

const AddOrEditProductPage = () => {
  const navigate = useNavigate();
  const { data: entity } = useEntity();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const productId = queryParams.get("id");
  const isEditMode = !!productId;

  // Product loading state
  const [product, setProduct] = useState<Product | null>(null);
  const [isLoading, setIsLoading] = useState(isEditMode);
  const [existingImages, setExistingImages] = useState<string[]>([]);
  const [productLoaded, setProductLoaded] = useState(false);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [productImages, setProductImages] = useState<File[]>([]);
  const [isCompressingProductImages, setIsCompressingProductImages] = useState(false);

  // Variant system state
  const [variantConfig, setVariantConfig] = useState<ProductVariantConfig>({
    hasVariants: false,
    attributes: [],
    variantOptions: {},
    variants: []
  });

  // Explicit, admin-controlled decision: does this product have variants?
  // Drives whether we show the simple pricing section or the variant builder.
  const [hasVariants, setHasVariants] = useState(false);

  const { user } = useAuth();

  // Permission check function for delivery settings
  const canAccessDeliverySettings = () => {
    if (!user?.email) return false;

    // 🔧 CUSTOMIZE THIS SECTION - Choose one of the options below:

    // Option 1: Specific emails (uncomment and modify)
    const allowedEmails = [
      'panktihouse@gmail.com',
      'abhishekmaher1@gmail.com',
      'abhishekmaher100meena@gmail.com',
      'ishaanagarwal57@gmail.com',
      'argymwala@gmail.com',
      'ammuchandammu@gmail.com',
      'meena.khyati@gmail.com'
    ];
    return allowedEmails.includes(user.email);

    // Option 2: Email domain check (uncomment and modify)
    // return user.email.endsWith('@yourcompany.com');

    // Option 3: Multiple domain check (uncomment and modify)
    // const allowedDomains = ['@yourcompany.com', '@admin.com'];
    // return allowedDomains.some(domain => user.email.endsWith(domain));

    // Option 4: Email pattern matching (uncomment and modify)
    // const adminPattern = /^(admin|owner|manager)@/;
    // return adminPattern.test(user.email);

    // 🚨 CURRENT SETTING: Allow all authenticated users
    // Change this to one of the options above for restriction
    return true;
  };

  // Permission check function for author field
  const canAccessAuthorField = () => {
    if (!user?.email) return false;

    // 🔧 CUSTOMIZE THIS SECTION - Choose one of the options below:

    // Option 1: Specific emails (uncomment and modify)
    const allowedEmails = [
      'panktihouse@gmail.com',
      'abhishekmaher1@gmail.com',
      'abhishekmaher100meena@gmail.com',
      'ishaanagarwal57@gmail.com',
      'argymwala@gmail.com',
      'ammuchandammu@gmail.com',
      'meena.khyati@gmail.com'
    ];
    return allowedEmails.includes(user.email);

    // Option 2: Email domain check (uncomment and modify)
    // return user.email.endsWith('@yourcompany.com');

    // Option 3: Multiple domain check (uncomment and modify)
    // const allowedDomains = ['@yourcompany.com', '@admin.com'];
    // return allowedDomains.some(domain => user.email.endsWith(domain));

    // Option 4: Email pattern matching (uncomment and modify)
    // const adminPattern = /^(admin|editor|manager)@/;
    // return adminPattern.test(user.email);

    // 🚨 CURRENT SETTING: Restrict to specific emails
    // return true; // Uncomment this to allow all authenticated users
  };

  // Toast store
  const { showToast } = useToastStore();

  const { register, handleSubmit, reset, control, watch, setValue, formState: { errors } } = useForm<ProductForm>({
    defaultValues: {
      title: "",
      description: "",
      author: "",
      mrp: undefined,
      sellingPrice: undefined,
      sku: "",
      stock: undefined,
      deliveryCharge: undefined,
      hasVariants: false,
      minOrderQty: undefined,
    },
  });

  // Watched values used to auto-suggest a SKU and to copy parent price/stock
  // down onto variants.
  const watchedTitle = watch("title");
  const watchedMrp = watch("mrp");
  const watchedSellingPrice = watch("sellingPrice");
  const watchedStock = watch("stock");

  // Build a SKU suggestion from the product name, e.g. "CLASSIC-T-SHIRT".
  const suggestedSku = (watchedTitle || "")
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

  // Load product data for edit mode
  useEffect(() => {
    const loadProduct = async () => {
      if (!isEditMode || !productId) return;
      if (productLoaded) return; // Prevent multiple loads

      try {
        setIsLoading(true);
        const productData = await ProductService.getProductById(productId);
        setProduct(productData);

        // Set existing images
        if (productData.images) {
          setExistingImages(productData.images);
        }

        // Populate form with product data - only once when product is first loaded
        // Prefer the stored discounted (selling) price; fall back to deriving it
        // from mrp + discountPercent for older records.
        const derivedSellingPrice = productData.discountedPrice
          ? productData.discountedPrice
          : (productData.mrp ?? 0) - ((productData.mrp ?? 0) * (productData.discountPercent || 0)) / 100;

        reset({
          title: productData.title,
          description: productData.description,
          author: productData.config?.author || "",
          mrp: productData.mrp,
          sellingPrice: derivedSellingPrice,
          sku: productData.sku || "",
          stock: productData.stock,
          deliveryCharge: productData.deliveryCharge,
          hasVariants: productData.hasVariants || false,
          minOrderQty: (productData as any).minOrderQty || undefined,
        });

        // Reflect the saved variant state in the toggle.
        setHasVariants(productData.hasVariants || false);

        // Populate variant config if product has variants
        if (productData.hasVariants && productData.variants) {
          setVariantConfig({
            hasVariants: true,
            attributes: productData.attributes || [],
            variantOptions: productData.variantOptions || {},
            variants: productData.variants.map(variant => ({
              id: variant._id, // Map _id to id for frontend compatibility
              attributes: variant.attributes,
              mrp: variant.mrp, // Map price to mrp for form compatibility
              discountPercent: variant.discountPercent,
              stock: variant.stock,
              sku: variant.sku,
              existingImages: variant.images || [], // Load existing images from backend
              images: [], // New uploads start empty
              discountedPrice: variant.discountedPrice
            }))
          });
        }

        setProductLoaded(true);
      } catch (error) {
        console.error("Failed to load product:", error);
        showToast("Failed to load product. Please try again.", "error");
        navigate("/products");
      } finally {
        setIsLoading(false);
      }
    };

    loadProduct();
  }, [isEditMode, productId, productLoaded, reset, showToast, navigate]);

  // Helper function for generating variant combinations (used in onSubmit)
  const generateVariantCombinations = () => {
    const attributes = variantConfig.attributes;
    const options = variantConfig.variantOptions;

    if (attributes.length === 0) return [];

    const generateCombinations = (attrs: string[], current: Record<string, string> = {}): Record<string, string>[] => {
      if (attrs.length === 0) return [current];

      const [attr, ...rest] = attrs;
      const values = options[attr] || [];
      const combinations: Record<string, string>[] = [];

      values.forEach(value => {
        combinations.push(...generateCombinations(rest, { ...current, [attr]: value }));
      });

      return combinations;
    };

    return generateCombinations(attributes);
  };

  // Product image logic (for non-variant products)
  const handleProductImageChange = async (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      const files = Array.from(event.target.files);
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];
      const invalidFiles = files.filter(file => !allowedTypes.includes(file.type));
      if (invalidFiles.length > 0) {
        showToast("Some files are not valid images. Please use JPG, PNG, or WebP files.", "error");
        return;
      }
      setIsCompressingProductImages(true);
      try {
        const { compressImageFiles } = await import("../../utils/imageCompression");
        const compressed = await compressImageFiles(files);
        setProductImages(prev => [...prev, ...compressed]);
      } finally {
        setIsCompressingProductImages(false);
      }
    }
  };

  const removeProductImage = (index: number) => {
    setProductImages(productImages.filter((_, i) => i !== index));
  };

  // Remove existing image
  const removeExistingImage = (index: number) => {
    setExistingImages(existingImages.filter((_, i) => i !== index));
  };

  const onSubmit = async (data: ProductForm) => {
    try {
      setIsSubmitting(true);

      console.log("data", data);

      // `hasVariants` (component state) is the admin's explicit choice. In variant
      // mode the MRP/Selling/Stock fields act as the default seeded onto each variant.

      // Validate base fields for all products
      const mrpValue = data.mrp === "" || data.mrp === undefined ? 0 : Number(data.mrp);
      // Selling price defaults to MRP (no discount) when left blank.
      const sellingPriceValue = data.sellingPrice === "" || data.sellingPrice === undefined ? mrpValue : Number(data.sellingPrice);
      const stockValue = data.stock === "" || data.stock === undefined ? 0 : Number(data.stock);
      const deliveryChargeValue = data.deliveryCharge === "" || data.deliveryCharge === undefined ? 0 : Number(data.deliveryCharge);
      const minOrderQtyValue = data.minOrderQty === "" || data.minOrderQty === undefined ? undefined : Number(data.minOrderQty);

      // Derive discount percent from MRP + Selling Price for the backend contract.
      const discountValue = mrpValue > 0 ? ((mrpValue - sellingPriceValue) / mrpValue) * 100 : 0;

      if (!mrpValue || mrpValue < 0) {
        showToast("Please enter a valid MRP.", "error");
        return;
      }
      if (stockValue < 0) {
        showToast("Stock cannot be negative.", "error");
        return;
      }
      if (sellingPriceValue < 0) {
        showToast("Selling price cannot be negative.", "error");
        return;
      }
      if (sellingPriceValue > mrpValue) {
        showToast("Selling price cannot be greater than MRP.", "error");
        return;
      }
      if (deliveryChargeValue < 0) {
        showToast("Delivery charge cannot be negative.", "error");
        return;
      }

      // Validate minimum order quantity if provided
      if (minOrderQtyValue !== undefined && minOrderQtyValue < 1) {
        showToast("Minimum order quantity must be at least 1.", "error");
        return;
      }

      console.log("hasVariants", hasVariants);

      // Generate the complete product document
      const slug = data.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

      let productDocument: any = {
        // Basic info
        id: productId || '',
        title: data.title,
        description: data.description,
        config: {},

        // SEO & control
        slug,
        status: "draft",
        visibility: "public"
      };

      // Add author field if user has permission and field is provided
      if (canAccessAuthorField() && data.author) {
        productDocument.config.author = data.author;
      }

      if (hasVariants) {
        if (variantConfig.attributes.length === 0) {
          showToast("Please add at least one attribute.", "error");
          return;
        }

        const emptyAttributes = variantConfig.attributes.filter(attr =>
          !variantConfig.variantOptions[attr] || variantConfig.variantOptions[attr].length === 0
        );
        if (emptyAttributes.length > 0) {
          showToast(`Please add values for: ${emptyAttributes.join(', ')}`, "error");
          return;
        }

        if (variantConfig.variants.length === 0) {
          showToast("Please generate variant combinations first.", "error");
          return;
        }

        const invalidVariants = variantConfig.variants.filter(variant =>
          variant.mrp <= 0 || variant.stock < 0 || variant.discountedPrice < 0 || variant.discountedPrice > variant.mrp
        );
        if (invalidVariants.length > 0) {
          showToast("Please fill in all variant details correctly. Selling price must be between 0 and the variant's MRP.", "error");
          return;
        }

        // Variants inherit the main product photos when they have none of their own
        // (handled in ProductService), so per-variant images are optional here.

        // Generate combinations from the current variant config
        const combinations = generateVariantCombinations();

        // Process variants and calculate prices
        const processedVariants = combinations.map((combo, index) => {
          const existingVariant = variantConfig.variants.find(v =>
            Object.keys(combo).every(key => v.attributes[key] === combo[key])
          );

          console.log(`Processing variant ${index}:`, {
            combo,
            existingVariant: existingVariant ? {
              id: existingVariant.id,
              attributes: existingVariant.attributes,
              images: existingVariant.images?.length || 0
            } : 'Not found'
          });


          // Use the admin-entered / manager-suggested (title-based) SKU. If it's
          // missing, send it blank so the backend generates a guaranteed-unique
          // one — never fabricate an attribute-only SKU here, since those collide
          // across products (every "blue" variant would become PROD-COL-BLUE).
          const sku = existingVariant?.sku?.trim() || "";

          // Generate or preserve ID
          const variantId = existingVariant?.id || `variant_${Date.now()}_${index}`;

          const variantMrp = existingVariant?.mrp !== undefined ? existingVariant.mrp : 0;

          return {
            id: variantId, // Include variant ID
            attributes: combo,
            mrp: variantMrp,
            stock: existingVariant?.stock !== undefined ? existingVariant.stock : 0,
            sku,
            existingImages: existingVariant?.existingImages || [], // Keep existing images
            images: existingVariant?.images || [], // Keep new uploads
            discountPercent: existingVariant?.discountPercent !== undefined ? existingVariant.discountPercent : 0,
            discountedPrice: existingVariant?.discountedPrice !== undefined ? existingVariant.discountedPrice : variantMrp
          };
        });


        productDocument = {
          ...productDocument,
          // Variant support
          hasVariants: true,
          attributes: variantConfig.attributes,
          variantOptions: variantConfig.variantOptions,
          variants: processedVariants,

          // Base product fields from form data
          mrp: mrpValue,
          stock: stockValue,
          discountPercent: discountValue,
          discountedPrice: sellingPriceValue,
          deliveryCharge: deliveryChargeValue,

          // Minimum order quantity
          minOrderQty: minOrderQtyValue,

          // Images (both existing URLs and new Files)
          images: [...existingImages, ...productImages]
        };
      } else {
        console.log("simple product");
        if (productImages.length === 0 && existingImages.length === 0) {
          showToast("Please add at least one product image.", "error");
          return;
        }
        // Simple product without variants. Use the admin-entered SKU when provided,
        // otherwise send it blank so the backend generates a guaranteed-unique one
        // — never fabricate a title-derived SKU here, since two products with the
        // same name would produce the same PROD-<TITLE> and collide (duplicate SKU).
        const sku = (data.sku || "").trim() || "";

        productDocument = {
          ...productDocument,
          // Simple product fallback
          hasVariants: false,
          mrp: mrpValue,
          stock: stockValue,
          sku,
          discountPercent: discountValue,
          discountedPrice: sellingPriceValue,
          deliveryCharge: deliveryChargeValue,

          // Minimum order quantity
          minOrderQty: minOrderQtyValue,

          // Images (both existing URLs and new Files)
          images: [...existingImages, ...productImages]
        };
      }

      // Show the upload bar only when there are new files to upload; otherwise
      // (e.g. editing with only existing image URLs) keep the plain "Saving…" state.
      const hasNewFiles =
        productImages.length > 0 ||
        variantConfig.variants.some((v) => (v.images?.length || 0) > 0);
      if (hasNewFiles) setUploadProgress(0);

      // Call the product service to add or update the product
      const result = await ProductService.addOrUpdateProduct({
        data: productDocument,
        onProgress: (percent) => setUploadProgress(percent),
      });

      if (!isEditMode) {
        trackEvent('product_added', {
          product_name: data.title,
          has_variants: hasVariants,
          has_discount: discountValue > 0,
        });

        // Meta pixel — merchant acquisition funnel: first product added. The
        // browser can't authoritatively know it's the store's first product, so
        // we fire at most once per browser (localStorage guard) using the stable
        // eventID product_first_${entityId}. The backend CAPI fires the same id
        // authoritatively (only when the server-side product count is 1), and
        // Meta deduplicates the two by event id — so a repeat here never double-counts.
        if (entity?.entityId != null) {
          const guardKey = `meta_first_product_${entity.entityId}`;
          if (!localStorage.getItem(guardKey)) {
            localStorage.setItem(guardKey, '1');
            setMetaUser({ phone: entity.mobile, email: entity.email, externalId: entity.entityId });
            // Name + eventID MUST match the backend CAPI event exactly
            // (metaConversionService: "AddProduct" / `add_product_<entityId>`) so
            // Meta deduplicates the browser and server copies.
            trackMetaCustom('AddProduct', `add_product_${entity.entityId}`, {
              content_name: data.title,
            });
          }
        }
      }

      // Inventory changed — drop the cached stats and mark them stale so the
      // inventory page recomputes the summary cards on next visit.
      clearInventoryStatsCache();
      queryClient.invalidateQueries({ queryKey: ["inventory-stats"] });

      showToast(`Product ${isEditMode ? "updated" : "added"} successfully`, "success");
      navigate("/products");

    } catch (error) {
      console.error("Form submission error:", error);
      // Surface the backend's specific message (e.g. a SKU conflict) instead of a
      // generic error — HttpService rethrows response.data.message as the Error.
      const message = error instanceof Error && error.message ? error.message : "Failed to save product. Please try again.";
      showToast(message, "error");
    } finally {
      setIsSubmitting(false);
      setUploadProgress(null);
    }
  };

  if (isEditMode && isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loading />
          <p className="mt-4 text-gray-600">Loading product details...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white pb-24">
      <div className="max-w-3xl mx-auto p-4 md:p-8">
        <form className="space-y-10">
          {/* Product Images Section */}
          <section className="bg-white rounded p-6 border border-gray-100 space-y-6">
            <div className="flex items-center gap-2 mb-2">
              <Image className="w-6 h-6 text-green-500" />
              <h3 className="text-xl font-bold text-gray-900">Product Images</h3>
            </div>
            <p className="text-gray-500 text-sm mb-4">Upload high-quality images to showcase your product.</p>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 flex flex-col items-center justify-center cursor-pointer hover:border-blue-400 transition bg-gray-50 mb-4 relative"
              onClick={() => document.getElementById('product-image-input')?.click()}
              onDragOver={e => { e.preventDefault(); e.stopPropagation(); }}
              onDrop={e => {
                e.preventDefault();
                e.stopPropagation();
                const files = Array.from(e.dataTransfer.files);
                const event = { target: { files } } as unknown as React.ChangeEvent<HTMLInputElement>;
                handleProductImageChange(event);
              }}
              tabIndex={0}
              aria-label="Upload product images"
            >
              <Image className="w-10 h-10 text-gray-400 mb-2" />
              <span className="text-gray-500 text-sm">Drag & drop images here, or <span className="text-blue-600 underline">click to upload</span></span>
              <span className="text-xs text-gray-400 mt-2">Images are compressed on upload</span>
              <input
                id="product-image-input"
                type="file"
                multiple
                accept="image/*"
                className="hidden"
                onChange={handleProductImageChange}
              />
              {isCompressingProductImages && (
                <div className="absolute inset-0 bg-white/70 flex items-center justify-center gap-2 rounded-lg">
                  <div className="h-4 w-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                  <span className="text-xs text-gray-600 font-medium">Compressing...</span>
                </div>
              )}
            </div>
            {(existingImages.length > 0 || productImages.length > 0) && (
              <div className="space-y-4">
                {/* Existing Images */}
                {existingImages.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Current Images</h4>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                      {existingImages.map((imageUrl, index) => (
                        <div key={`existing-${index}`} className="relative group rounded-lg overflow-hidden shadow border border-gray-200 bg-white">
                          <img
                            src={imageUrl}
                            alt={`Existing ${index}`}
                            className="object-cover w-full h-32 sm:h-40 transition-transform duration-300 group-hover:scale-105"
                          />
                          <button
                            type="button"
                            className="absolute top-2 right-2 text-white bg-red-500 hover:bg-red-600 rounded-full p-1 opacity-80 group-hover:opacity-100 transition-all duration-200 shadow"
                            onClick={e => { e.stopPropagation(); removeExistingImage(index); }}
                            aria-label="Remove existing image"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* New Images */}
                {productImages.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">New Images</h4>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                      {productImages.map((image, index) => (
                        <div key={`new-${index}`} className="relative group rounded-lg overflow-hidden shadow border border-gray-200 bg-white">
                          <img
                            src={URL.createObjectURL(image)}
                            alt={`New ${index}`}
                            className="object-cover w-full h-32 sm:h-40 transition-transform duration-300 group-hover:scale-105"
                          />
                          <button
                            type="button"
                            className="absolute top-2 right-2 text-white bg-red-500 hover:bg-red-600 rounded-full p-1 opacity-80 group-hover:opacity-100 transition-all duration-200 shadow"
                            onClick={e => { e.stopPropagation(); removeProductImage(index); }}
                            aria-label="Remove new image"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </section>

          {/* Product Details Section */}
          <section className="bg-white rounded p-6 border border-gray-100 space-y-6 relative">
            <div className="flex items-center gap-2 mb-2">
              <svg className="w-6 h-6 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
              </svg>
              <h2 className="text-2xl font-bold text-gray-900">Product Details</h2>
            </div>
            <p className="text-gray-500 text-sm mb-4">Enter the basic information about your product.</p>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Product Name</label>
                <input
                  type="text"
                  placeholder="e.g., Classic T-shirt"
                  className={`w-full px-4 py-3 rounded-lg border focus:ring-2 focus:ring-blue-200 focus:border-blue-500 bg-gray-50 transition-all duration-200 ${errors.title ? 'border-red-300 ring-red-100 focus:border-red-500 focus:ring-red-200' : 'border-gray-300'}`}
                  {...register("title", { required: "Product name is required" })}
                  aria-invalid={!!errors.title}
                  aria-describedby="product-name-help"
                />
                <p id="product-name-help" className="text-xs text-gray-400 mt-1">This will be shown to customers.</p>
                {errors.title && (
                  <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    {errors.title.message}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <Controller
                  name="description"
                  control={control}
                  defaultValue=""
                  rules={{
                    required: "Description is required",
                    validate: (value) => {
                      const plainText = value.replace(/<[^>]+>/g, '');
                      return plainText.trim().length > 0 || "Description cannot be empty";
                    }
                  }}
                  render={({ field }) => (
                    <div>
                      <ReactQuill
                        theme="snow"
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        placeholder="Describe your product..."
                        className={`bg-white ${errors.description ? 'border-red-300' : 'border-gray-300'}`}
                        style={{ height: '200px', marginBottom: '50px' }}
                        modules={{
                          toolbar: [
                            [{ 'header': [1, 2, 3, false] }],
                            ['bold', 'italic', 'underline'],
                            [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                            ['link'],
                            ['clean']
                          ],
                        }}
                      />
                    </div>
                  )}
                />
                <p id="product-desc-help" className="text-xs text-gray-400 mt-1">A good description helps customers decide.</p>
                {errors.description && (
                  <p className="mt-1 text-xs text-red-600 flex items-center gap-1"><Info className="w-4 h-4" />{errors.description.message}</p>
                )}
              </div>

              {/* Author Field - Conditional based on user email */}
              {canAccessAuthorField() && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Author</label>
                  <input
                    type="text"
                    placeholder="e.g., John Doe, Brand Name, or Product Creator"
                    className={`w-full px-4 py-3 rounded-lg border focus:ring-2 focus:ring-blue-200 focus:border-blue-500 bg-gray-50 transition-all duration-200 ${errors.author ? 'border-red-300 ring-red-100 focus:border-red-500 focus:ring-red-200' : 'border-gray-300'}`}
                    {...register("author")}
                    aria-invalid={!!errors.author}
                    aria-describedby="author-help"
                  />
                  <p id="author-help" className="text-xs text-gray-400 mt-1">Optional field to specify the product author or creator.</p>
                  {errors.author && (
                    <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
                      <Info className="w-4 h-4" />
                      {errors.author.message}
                    </p>
                  )}
                </div>
              )}
            </div>
          </section>

          {/* Variant decision — determines whether pricing is per-product or per-variant */}
          <section className="bg-white rounded p-6 border border-gray-100">
            <label className="flex items-start gap-3 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={hasVariants}
                onChange={(e) => setHasVariants(e.target.checked)}
                className="mt-1 h-5 w-5 rounded border-gray-300 text-pink-600 focus:ring-pink-500"
              />
              <span>
                <span className="flex items-center gap-2 text-lg font-bold text-gray-900">
                  <Palette className="w-5 h-5 text-pink-500" />
                  This product has variants
                </span>
                <span className="block text-gray-500 text-sm mt-1">
                  Turn on if the product comes in options like size or colour, each with its own price, stock and SKU. The price &amp; stock below become the defaults applied to every variant.
                </span>
              </span>
            </label>
          </section>

          {/* Pricing & Inventory Section (per-product, or defaults for variants) */}
          <section className="bg-white rounded p-6 border border-gray-100 space-y-6">
            <div className="flex items-center gap-2 mb-2">
              <Info className="w-6 h-6 text-yellow-500" />
              <h3 className="text-xl font-bold text-gray-900">{hasVariants ? "Default Price & Stock" : "Pricing & Inventory"}</h3>
            </div>
            <p className="text-gray-500 text-sm mb-4">
              {hasVariants
                ? "These seed every variant below — you can override price and stock per variant."
                : "Set the MRP, selling price, SKU, and available stock for this product."}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">MRP (₹)</label>
                <Controller
                  name="mrp"
                  control={control}
                  rules={{
                    required: "MRP is required",
                    min: { value: 0, message: "MRP cannot be negative" }
                  }}
                  render={({ field }) => (
                    <input
                      type="number"
                      placeholder="0.00"
                      className={`w-full px-4 py-3 rounded-lg border focus:ring-2 focus:ring-blue-200 focus:border-blue-500 bg-gray-50 transition-all duration-200 ${errors.mrp ? 'border-red-300 ring-red-100 focus:border-red-500 focus:ring-red-200' : 'border-gray-300'}`}
                      inputMode="decimal"
                      step="0.01"
                      value={field.value ?? ""}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === "") {
                          field.onChange("");
                        } else {
                          const numValue = parseFloat(value);
                          field.onChange(isNaN(numValue) ? "" : numValue);
                        }
                      }}
                      onWheel={e => (e.target as HTMLInputElement).blur()}
                      aria-invalid={!!errors.mrp}
                      aria-describedby="mrp-help"
                    />
                  )}
                />
                <p id="mrp-help" className="text-xs text-gray-400 mt-1">Maximum Retail Price.</p>
                {errors.mrp && (
                  <p className="mt-1 text-xs text-red-600 flex items-center gap-1"><Info className="w-4 h-4" />{errors.mrp.message}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Selling Price (₹)</label>
                <Controller
                  name="sellingPrice"
                  control={control}
                  rules={{
                    min: { value: 0, message: "Selling price cannot be negative" },
                    validate: (value) => {
                      if (value === "" || value === undefined) return true;
                      const mrp = Number(watchedMrp);
                      if (!isNaN(mrp) && Number(value) > mrp) return "Selling price cannot exceed MRP";
                      return true;
                    }
                  }}
                  render={({ field }) => (
                    <input
                      type="number"
                      placeholder="0.00"
                      className={`w-full px-4 py-3 rounded-lg border focus:ring-2 focus:ring-blue-200 focus:border-blue-500 bg-gray-50 transition-all duration-200 ${errors.sellingPrice ? 'border-red-300 ring-red-100 focus:border-red-500 focus:ring-red-200' : 'border-gray-300'}`}
                      inputMode="decimal"
                      step="0.01"
                      value={field.value ?? ""}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === "") {
                          field.onChange("");
                        } else {
                          const numValue = parseFloat(value);
                          field.onChange(isNaN(numValue) ? "" : numValue);
                        }
                      }}
                      onWheel={e => (e.target as HTMLInputElement).blur()}
                      aria-invalid={!!errors.sellingPrice}
                      aria-describedby="selling-price-help"
                    />
                  )}
                />
                <p id="selling-price-help" className="text-xs text-gray-400 mt-1">
                  {(() => {
                    const mrp = Number(watchedMrp);
                    const sp = Number(watchedSellingPrice);
                    if (!isNaN(mrp) && mrp > 0 && !isNaN(sp) && sp > 0 && sp <= mrp) {
                      const off = Math.round(((mrp - sp) / mrp) * 100);
                      return off > 0 ? `Price customers pay — ${off}% off MRP.` : "Price customers pay.";
                    }
                    return "Price customers pay. Leave blank for no discount.";
                  })()}
                </p>
                {errors.sellingPrice && (
                  <p className="mt-1 text-xs text-red-600 flex items-center gap-1"><Info className="w-4 h-4" />{errors.sellingPrice.message}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Inventory Quantity</label>
                <Controller
                  name="stock"
                  control={control}
                  rules={{
                    required: "Inventory is required",
                    min: { value: 0, message: "Inventory cannot be negative" }
                  }}
                  render={({ field }) => (
                    <input
                      type="number"
                      placeholder="Enter quantity"
                      value={field.value ?? ""}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === "") {
                          field.onChange("");
                        } else {
                          const numValue = parseInt(value);
                          field.onChange(isNaN(numValue) ? "" : numValue);
                        }
                      }}
                      className={`w-full px-4 py-3 rounded-lg border focus:ring-2 focus:ring-blue-200 focus:border-blue-500 bg-gray-50 transition-all duration-200 ${errors.stock ? 'border-red-300 ring-red-100 focus:border-red-500 focus:ring-red-200' : 'border-gray-300'}`}
                      min="0"
                      onWheel={e => (e.target as HTMLInputElement).blur()}
                      aria-invalid={!!errors.stock}
                      aria-describedby="inventory-help"
                    />
                  )}
                />
                <p id="inventory-help" className="text-xs text-gray-400 mt-1">How many items are in stock?</p>
              </div>
            </div>

            {/* SKU — per-product only; variant products use per-variant SKUs below */}
            {!hasVariants && (
              <div className="max-w-md">
                <label className="block text-sm font-medium text-gray-700 mb-1">SKU</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder={suggestedSku || "e.g., CLASSIC-T-SHIRT"}
                    className="flex-1 px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-200 focus:border-blue-500 bg-gray-50 transition-all duration-200 font-mono text-sm"
                    {...register("sku")}
                    aria-describedby="sku-help"
                  />
                  {suggestedSku && (
                    <button
                      type="button"
                      onClick={() => setValue("sku", suggestedSku, { shouldDirty: true })}
                      className="whitespace-nowrap px-3 py-2 text-xs font-medium text-blue-600 border border-blue-200 hover:bg-blue-50 rounded-lg transition"
                      title="Fill SKU from the product name"
                    >
                      Use suggestion
                    </button>
                  )}
                </div>
                <p id="sku-help" className="text-xs text-gray-400 mt-1">
                  Stock keeping unit. Leave blank to auto-generate from the product name.
                </p>
              </div>
            )}
          </section>

          {/* Product Variant Manager — only when the product has variants */}
          {hasVariants && (
            <section className="bg-white rounded p-6 border border-gray-100 space-y-6">
              <div className="flex items-center gap-2 mb-2">
                <Palette className="w-6 h-6 text-pink-500" />
                <h3 className="text-xl font-bold text-gray-900">Product Variants</h3>
              </div>
              <p className="text-gray-500 text-sm mb-4">Add options like size or colour, then set stock, price and SKU per variant. Blank rows inherit the default price &amp; stock above.</p>

              <ProductVariantManager
                variantConfig={variantConfig}
                onVariantConfigChange={(config: ProductVariantConfig) => { setVariantConfig(config); console.log("variantConfig", config) }}
                toast={{ showToast }}
                productTitle={watchedTitle}
                baseValues={{
                  mrp: watchedMrp === "" || watchedMrp === undefined ? undefined : Number(watchedMrp),
                  sellingPrice: watchedSellingPrice === "" || watchedSellingPrice === undefined ? undefined : Number(watchedSellingPrice),
                  stock: watchedStock === "" || watchedStock === undefined ? undefined : Number(watchedStock),
                }}
              />
            </section>
          )}

          {/* Minimum Order Quantity Section */}
          <section className="bg-white rounded p-6 border border-gray-100 space-y-6">
            <div className="flex items-center gap-2 mb-2">
              <svg className="w-6 h-6 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
              <h3 className="text-xl font-bold text-gray-900">Order Requirements</h3>
            </div>
            <p className="text-gray-500 text-sm mb-4">Set minimum order requirements for bulk purchases.</p>

            <div className="max-w-md">
              <label className="block text-sm font-medium text-gray-700 mb-1">Minimum Order Quantity</label>
              <Controller
                name="minOrderQty"
                control={control}
                rules={{
                  min: { value: 1, message: "Minimum order quantity must be at least 1" }
                }}
                render={({ field }) => (
                  <input
                    type="number"
                    placeholder="Leave empty for no minimum (e.g., 10 for bulk orders)"
                    className={`w-full px-4 py-3 rounded-lg border focus:ring-2 focus:ring-purple-200 focus:border-purple-500 bg-gray-50 transition-all duration-200 ${errors.minOrderQty ? 'border-red-300 ring-red-100 focus:border-red-500 focus:ring-red-200' : 'border-gray-300'}`}
                    value={field.value ?? ""}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === "") {
                        field.onChange("");
                      } else {
                        const numValue = parseInt(value);
                        field.onChange(isNaN(numValue) ? "" : numValue);
                      }
                    }}
                    min="1"
                    onWheel={e => (e.target as HTMLInputElement).blur()}
                    aria-invalid={!!errors.minOrderQty}
                    aria-describedby="min-order-qty-help"
                  />
                )}
              />
              <p id="min-order-qty-help" className="text-xs text-gray-400 mt-1">Optional: Set the minimum number of units customers must order. Leave empty if no minimum is required.</p>
              {errors.minOrderQty && (
                <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
                  <Info className="w-4 h-4" />
                  {errors.minOrderQty.message}
                </p>
              )}
            </div>
          </section>

          {/* Delivery Configuration Section - Conditional based on user email */}
          {canAccessDeliverySettings() && (
            <section className="bg-white rounded p-6 border border-gray-100 space-y-6">
              <div className="flex items-center gap-2 mb-2">
                <svg className="w-6 h-6 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2 2v-5m16 0h-2M4 13h2m13-8V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v1M7 8h10l-1 8H8l-1-8z" />
                </svg>
                <h3 className="text-xl font-bold text-gray-900">Delivery Configuration</h3>
              </div>
              <p className="text-gray-500 text-sm mb-4">Set specific delivery charges for this product if required.</p>
              <div className="max-w-md">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Delivery Charge (₹)</label>
                  <Controller
                    name="deliveryCharge"
                    control={control}
                    rules={{
                      min: { value: 0, message: "Delivery charge cannot be negative" }
                    }}
                    render={({ field }) => (
                      <input
                        type="number"
                        placeholder="0.00"
                        className={`w-full px-4 py-3 rounded-lg border focus:ring-2 focus:ring-blue-200 focus:border-blue-500 bg-gray-50 transition-all duration-200 ${errors.deliveryCharge ? 'border-red-300 ring-red-100 focus:border-red-500 focus:ring-red-200' : 'border-gray-300'}`}
                        inputMode="decimal"
                        step="0.01"
                        value={field.value ?? ""}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === "") {
                            field.onChange("");
                          } else {
                            const numValue = parseFloat(value);
                            field.onChange(isNaN(numValue) ? "" : numValue);
                          }
                        }}
                        onWheel={e => (e.target as HTMLInputElement).blur()}
                        aria-invalid={!!errors.deliveryCharge}
                        aria-describedby="delivery-charge-help"
                      />
                    )}
                  />
                  <p id="delivery-charge-help" className="text-xs text-gray-400 mt-1">Leave empty for free delivery or to use default store delivery charges.</p>
                  {errors.deliveryCharge && (
                    <p className="mt-1 text-xs text-red-600 flex items-center gap-1"><Info className="w-4 h-4" />{errors.deliveryCharge.message}</p>
                  )}
                </div>
              </div>
            </section>
          )}
        </form>
      </div>

      {/* Fixed Action Buttons at Bottom */}
      <div className="fixed bottom-0 left-0 w-full bg-white border-t border-gray-200 shadow-lg z-50 p-2 sm:p-4 lg:left-64 lg:w-[calc(100%-16rem)]">
        <div className="max-w-3xl mx-auto">
          {/* Upload progress bar (shown while new images are uploading) */}
          {isSubmitting && uploadProgress !== null && (
            <div className="mb-2 sm:mb-3">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-medium text-gray-600">
                  {uploadProgress < 100 ? "Uploading images…" : "Finalizing…"}
                </span>
                <span className="text-xs font-semibold text-blue-600">{uploadProgress}%</span>
              </div>
              <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-blue-600 rounded-full transition-all duration-200 ease-out"
                  style={{ width: `${uploadProgress}%` }}
                  role="progressbar"
                  aria-valuenow={uploadProgress}
                  aria-valuemin={0}
                  aria-valuemax={100}
                />
              </div>
            </div>
          )}
          <div className="flex gap-2 sm:gap-4">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="flex-1 px-3 py-2 sm:px-6 sm:py-3 text-sm sm:text-base text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg sm:rounded-xl transition duration-200 font-medium focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              onClick={handleSubmit(onSubmit)}
              className="flex-1 px-3 py-2 sm:px-6 sm:py-3 text-sm sm:text-base bg-blue-600 text-white rounded-lg sm:rounded-xl hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed transition duration-300 font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 flex items-center justify-center shadow-md hover:shadow-lg transform hover:-translate-y-0.5 active:translate-y-0"
            >
              {isSubmitting ? (
                <div className="flex items-center">
                  <Loading />
                  <span className="ml-1 sm:ml-2 text-xs sm:text-sm">
                    {uploadProgress !== null && uploadProgress < 100
                      ? `Uploading ${uploadProgress}%`
                      : "Saving..."}
                  </span>
                </div>
              ) : (
                <div className="flex items-center">
                  <span className="text-xs sm:text-sm">Save {isEditMode ? "Changes" : "Product"}</span>
                </div>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddOrEditProductPage;
